import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, MagnifyingGlass, X, Truck } from "@phosphor-icons/react";

const empty = { name: "", contact_name: "", phone: "", email: "", tax_id: "", address: "", products_supplied: "", notes: "" };

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const { data } = await api.get("/suppliers");
    setSuppliers(data);
  };
  useEffect(() => { load(); }, []);

  const filtered = suppliers.filter((s) => {
    const q = query.toLowerCase();
    return !q || s.name.toLowerCase().includes(q) || s.tax_id?.includes(q) || s.contact_name?.toLowerCase().includes(q);
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.put(`/suppliers/${editing}`, form);
      else await api.post("/suppliers", form);
      toast.success(editing ? "Proveedor actualizado" : "Proveedor creado");
      setShowForm(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 md:p-10" data-testid="suppliers-page">
      <header className="mb-6 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Cadena de suministro</p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold">Proveedores</h1>
          <p className="text-zinc-400 mt-1 text-sm">{suppliers.length} proveedor(es) registrados — para seguridad y trazabilidad</p>
        </div>
        <button onClick={() => { setEditing(null); setForm(empty); setShowForm(true); }}
          data-testid="supplier-new-btn"
          className="bg-brand-gradient text-black font-semibold px-5 py-2.5 rounded-md flex items-center gap-2 shadow-cyan-glow">
          <Plus size={18} weight="bold" /> Nuevo proveedor
        </button>
      </header>

      <div className="relative flex-1 max-w-md mb-4">
        <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar..."
          className="w-full bg-[#0F1115] border border-white/10 rounded-md pl-10 pr-4 py-2 text-sm" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.length === 0 ? (
          <div className="md:col-span-3 glass-card rounded-xl p-12 text-center text-zinc-500">
            <Truck size={48} className="mx-auto mb-4 opacity-30" />
            <p>No hay proveedores registrados</p>
          </div>
        ) : filtered.map((s) => (
          <div key={s.supplier_id} className="glass-card rounded-xl p-5 hover:border-turquoise/30 transition group">
            <div className="flex items-start justify-between mb-3">
              <div>
                <h3 className="font-heading font-semibold text-white">{s.name}</h3>
                {s.contact_name && <p className="text-xs text-zinc-500 font-mono">{s.contact_name}</p>}
              </div>
              <Truck size={20} className="text-turquoise opacity-60" />
            </div>
            <div className="space-y-1 text-xs font-mono text-zinc-400">
              {s.tax_id && <div><span className="text-zinc-600">CUIT/RUC:</span> {s.tax_id}</div>}
              {s.phone && <div><span className="text-zinc-600">Tel:</span> {s.phone}</div>}
              {s.email && <div className="truncate"><span className="text-zinc-600">Email:</span> {s.email}</div>}
              {s.address && <div><span className="text-zinc-600">Dir:</span> {s.address}</div>}
            </div>
            {s.products_supplied && (
              <p className="text-xs text-zinc-500 mt-3 line-clamp-2">{s.products_supplied}</p>
            )}
            <div className="flex gap-2 mt-4 pt-3 border-t border-white/5 opacity-0 group-hover:opacity-100 transition">
              <button onClick={() => { setEditing(s.supplier_id); setForm(s); setShowForm(true); }}
                className="text-xs text-zinc-400 hover:text-turquoise flex items-center gap-1">
                <PencilSimple size={12} /> Editar
              </button>
              <button onClick={async () => {
                if (!confirm("¿Eliminar proveedor?")) return;
                await api.delete(`/suppliers/${s.supplier_id}`);
                toast.success("Eliminado"); load();
              }} className="text-xs text-zinc-400 hover:text-red-400 flex items-center gap-1">
                <Trash size={12} /> Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="glass-card rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="supplier-form-modal">
            <div className="flex items-center justify-between p-5 border-b border-white/5">
              <h3 className="font-heading text-lg font-semibold">{editing ? "Editar" : "Nuevo"} proveedor</h3>
              <button onClick={() => setShowForm(false)}><X size={20} /></button>
            </div>
            <form onSubmit={submit} className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Razón social *</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="supplier-name-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Contacto</label>
                <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">CUIT / RUC</label>
                <input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
                  data-testid="supplier-taxid-input"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Teléfono</label>
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Email</label>
                <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Dirección</label>
                <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs uppercase tracking-wider text-zinc-400 font-mono">Productos que suministra</label>
                <textarea value={form.products_supplied} onChange={(e) => setForm({ ...form, products_supplied: e.target.value })} rows="2"
                  className="mt-1.5 w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="md:col-span-2 flex gap-3 pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="flex-1 bg-white/5 border border-white/10 py-2.5 rounded-md">Cancelar</button>
                <button type="submit" data-testid="supplier-save-btn" className="flex-1 bg-brand-gradient text-black font-semibold py-2.5 rounded-md">
                  {editing ? "Actualizar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
